/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useState } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';

import { ConfigRevision, Node, NodeConfig } from '@/client';
import { BackButton } from '@/components/Buttons/BackButton';
import { ConfigWizardOverlay } from '@/components/ConfigWizardOverlay/ConfigWizardOverlay';
import { ContentContainer } from '@/components/ContentContainer';
import { HTTPError } from '@/components/HTTPError';
import { ArchivedNodeBanner } from '@/components/Node/ArchivedNodeBanner';
import {
  ExitMaintenanceModeModal,
  MaintenanceMode,
  MaintenanceModeBanner,
  MaintenanceModeButton,
  MaintenanceModeModal,
} from '@/components/Node/MaintenanceMode';
import { NodeState } from '@/components/Node/NodeState';
import { NodeTransitionIndicator } from '@/components/Node/NodeTransitionIndicator';
import { RejectedStateAlerts } from '@/components/Node/RejectedStateAlerts';
import { QueryWrapper } from '@/components/QueryWrapper';
import { useMessage } from '@/hooks/message';
import { useAssignConfigRevisionMutation } from '@/hooks/mutation/useAssignConfigRevisionMutation';
import { useUpdateNodeMutation } from '@/hooks/mutation/useUpdateNodeMutation';
import { useNodeByIdQuery } from '@/hooks/query/useNodeByIdQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { NodeDetailsHeader } from '@/pages/NodeDetails/NodeDetailsHeader';
import { NodeDetailsOverview } from '@/pages/NodeDetails/NodeDetailsOverview';
import { NodeDetailsServerInventory } from '@/pages/NodeDetails/NodeDetailsServerInventory';
import { NodeDetailsTelemetry } from '@/pages/NodeDetails/NodeDetailsTelemetry';
import { Container } from '@/primitives/Container';
import { Skeleton } from '@/primitives/Skeleton';
import { Tabs } from '@/primitives/Tabs';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import './NodeDetails.css';

export type NodeDetailsPageProps = {
  nodeId: string;
};

const NODE_DETAILS_TABS = ['overview', 'serverInventory', 'telemetry'] as const;
type NodeDetailsTab = (typeof NODE_DETAILS_TABS)[number];

export function NodeDetailsPage({ nodeId }: NodeDetailsPageProps) {
  const { t } = useMimicTranslation('node');
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const [message] = useMessage();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const activeTab: NodeDetailsTab = NODE_DETAILS_TABS.includes(tabParam as NodeDetailsTab)
    ? (tabParam as NodeDetailsTab)
    : 'overview';

  const onTabChange = (tab: string) => {
    setSearchParams(
      (prev) => {
        prev.set('tab', tab);
        return prev;
      },
      { replace: true },
    );
  };
  const [nodeToShow, setNodeToShow] = useState<Node | undefined>();
  const [selectedNodeConfig, setSelectedNodeConfig] = useState<NodeConfig | undefined>();
  const [selectedConfigRevision, setSelectedConfigRevision] = useState<ConfigRevision | undefined>();
  const nodeQuery = useNodeByIdQuery({ nodeId });

  const { mutate: assignConfigRevision } = useAssignConfigRevisionMutation({
    onError: () => {
      message.error(t('feedback.assignNodeConfigError'));
    },
  });

  const { mutate } = useUpdateNodeMutation({
    nodeId,
    onSuccess: () => message.success(t('feedback.nodeUpdated')),
    onError: () => message.error(t('feedback.updateFailed')),
  });

  if (nodeQuery.isError) {
    if (nodeQuery.error.status === 404) {
      return <HTTPError httpStatus="404" />;
    }
    return <HTTPError httpStatus="500" />;
  }

  if (!nodeQuery.data?.data) {
    return <Skeleton loading />;
  }

  const nodeTags = nodeQuery.data!.data.tags || [];

  const onNameChange = (newNodeName: string) => {
    mutate({ name: newNodeName });
  };

  if (!tenantID) return null;

  const onCloseConfigWizard = () => {
    setNodeToShow(undefined);
  };

  const onAssignConfigRevision = async (node: Node, configRevision: ConfigRevision) => {
    assignConfigRevision({
      nodeIDs: [node.id],
      configID: configRevision.nodeConfigId!,
      revisionNumber: configRevision.revisionNumber,
    });

    setNodeToShow(undefined);
  };

  return (
    <ContentContainer width="100%">
      <Container>
        <div style={{ marginBottom: '16px' }}>
          <BackButton to={`/tenants/${tenantID}/nodes`} label={t('back')} />
        </div>
      </Container>
      <QueryWrapper queryResult={nodeQuery}>
        {(nodeData) => (
          <MaintenanceMode nodeId={nodeId} node={nodeData.data}>
            {(maintenance) => {
              const tabs = [
                {
                  key: 'overview',
                  label: t('overview'),
                  children: (
                    <NodeDetailsOverview
                      nodeId={nodeId}
                      nodeData={nodeData}
                      nodeQuery={nodeQuery}
                      setNodeToShow={setNodeToShow}
                      setSelectedNodeConfig={setSelectedNodeConfig}
                      setSelectedConfigRevision={setSelectedConfigRevision}
                      tenantID={tenantID}
                    />
                  ),
                },
                {
                  key: 'serverInventory',
                  label: t('serverInventory'),
                  children: <NodeDetailsServerInventory nodeId={nodeId} />,
                },
                {
                  key: 'telemetry',
                  label: t('telemetry'),
                  children: <NodeDetailsTelemetry nodeId={nodeId} />,
                },
              ];

              return (
                <>
                  <NodeDetailsHeader
                    nodeData={nodeData}
                    nodeTags={nodeTags}
                    tenantID={tenantID}
                    onNameChange={onNameChange}
                    nodeId={nodeId}
                    maintenanceModeButton={
                      !nodeData.data.dateArchived ? <MaintenanceModeButton maintenance={maintenance} /> : undefined
                    }
                  />
                  <MaintenanceModeBanner maintenance={maintenance} />
                  <ArchivedNodeBanner node={nodeData.data} />
                  <NodeTransitionIndicator node={nodeData.data} />
                  <RejectedStateAlerts node={nodeData.data} />
                  <Tabs
                    items={tabs}
                    activeKey={activeTab}
                    onChange={onTabChange}
                    style={{ paddingTop: '32px' }}
                    className="details-tabs"
                    tabBarExtraContent={<NodeState isLoading={nodeQuery.isPending} node={nodeData.data} />}
                  />
                  <MaintenanceModeModal maintenance={maintenance} />
                  <ExitMaintenanceModeModal maintenance={maintenance} />
                </>
              );
            }}
          </MaintenanceMode>
        )}
      </QueryWrapper>
      {nodeToShow ? (
        <ConfigWizardOverlay
          node={nodeToShow}
          defaultNodeConfig={selectedNodeConfig}
          defaultConfigRevision={selectedConfigRevision}
          onCancel={onCloseConfigWizard}
          onAssign={onAssignConfigRevision}
        />
      ) : null}
      <Outlet />
    </ContentContainer>
  );
}
